import React, {useState} from 'react';
import { Maximize, ChevronUp, ChevronDown, Calendar as CalendarIcon } from 'lucide-react';
import { RoutingDecision, Shipment } from '../models/DeliveryOptionDetailsModel';

interface RoutingSectionProps {
  ship: Shipment;
  shipmentId: string;  
  routing: RoutingDecision;  
}

/** --- Donut Chart for Volume Sharing --- */
const VolumeSharingDonut = ({ sharing }: { sharing: Record<string, number> }) => {
    if (!sharing || Object.keys(sharing).length === 0) return null;
    const entries = Object.entries(sharing);
    const total = entries.reduce((acc, [_, val]) => acc + val, 0);
    
    let cumulativePercent = 0;
    const colors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#064e3b"];
  
    return (
      <div className="flex items-center gap-4 mt-1">
        <svg width="50" height="50" viewBox="0 0 42 42" className="shrink-0">
          <circle cx="21" cy="21" r="15.91549430918954" fill="transparent" stroke="#e2e8f0" strokeWidth="5"></circle>
          {entries.map(([plant, val], idx) => {
            const percentage = (val / total) * 100;
            const dashArray = `${percentage} ${100 - percentage}`;
            const dashOffset = 100 - cumulativePercent + 25; 
            cumulativePercent += percentage;
            return (
              <circle
                key={plant}
                cx="21"
                cy="21"
                r="15.91549430918954"
                fill="transparent"
                stroke={colors[idx % colors.length]}
                strokeWidth="5"
                strokeDasharray={dashArray}
                strokeDashoffset={dashOffset}
              ></circle>
            );
          })}
        </svg>
        <div className="flex flex-col gap-0.5">
          {entries.map(([plant, val], idx) => (
            <div key={plant} className="flex items-center gap-1.5 text-[9px] font-black uppercase">
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors[idx % colors.length] }}></div>
              <span className="text-slate-500">{plant}:</span>
              <span className="text-black">{val}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

const RoutingSection: React.FC<RoutingSectionProps> = ({
  ship,
  shipmentId,  
  routing  
}) => {
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => setOpenStates(prev => ({ ...prev, [id]: !prev[id] }));
  return (
    <div className="rounded-lg border-2 border-teal-200 overflow-hidden bg-white shadow-sm">
                        <div 
                          style={{ 
                            display: 'flex', 
                            flexDirection: 'row', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '10px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                          className="hover:bg-slate-50"
                          onClick={() => toggle(shipmentId + 'rt')}
                        >
                          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                            <Maximize size={12} className="text-blue-600 shrink-0" />
                            <span className="text-[11px] font-bold text-teal-950 uppercase shrink-0">Routing</span>
                            <span className="text-[10px] font-mono font-black text-blue-600 opacity-70">
                              [{ship?.PlantCode} / {routing?.RoutingRule?.VolumeSharing?.[ship?.PlantCode] || 100}%]
                            </span>
                          </div>
                          {openStates[shipmentId + 'rt'] ? <ChevronUp size={14} className="text-teal-900 shrink-0" /> : <ChevronDown size={14} className="text-teal-900 shrink-0" />}
                        </div>
                        {openStates[shipmentId + 'rt'] && (
                          <div className="px-4 pb-4 pt-3 border-t border-teal-100 bg-teal-50/30 text-[11px]">
                            <table className="w-full border-separate border-spacing-y-1 text-black">
                              <tbody>
                                <tr>
                                  <td className="w-[100px] text-slate-500 font-bold uppercase">Production Site</td>
                                  <td className="w-[10px] text-slate-400 font-bold">:</td>
                                  <td className="font-black text-blue-600 pl-2 uppercase">{ship?.PlantCode}</td>
                                </tr>
                                <tr>
                                  <td className="w-[100px] text-slate-500 font-bold uppercase">Rule Name</td>
                                  <td className="w-[10px] text-slate-400 font-bold">:</td>
                                  <td className="font-black text-black pl-2 leading-tight">
                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                      <span>{routing?.RoutingRule?.Name || "Default"}</span>
                                      {(() => {
                                        const rule = routing?.RoutingRule?.ActivityRules?.find(r => r.ActivityRuleType === "UnconditionalActivationRule");
                                        if (rule === undefined) return null;
                                        return (
                                          <div 
                                            title={rule.Active ? "Active" : "Inactive"}
                                            style={{ 
                                              width: '10px', 
                                              height: '10px', 
                                              borderRadius: '9999px', 
                                              backgroundColor: rule.Active ? '#10b981' : '#ef4444',
                                              flexShrink: 0
                                            }}
                                          />
                                        );
                                      })()}
                                        {(() => {
                                            const rule = routing?.RoutingRule?.ActivityRules?.find(r => r.ActivityRuleType === "ActiveBetweenDatesRule");
                                            if (rule === undefined) return null;
                                            if(rule.ActiveFrom === null && rule.ActiveUntil === null) return null;
                                            return (
                                              <div title={rule.ActiveFrom + "\r\n" + rule.ActiveUntil}>
                                                <CalendarIcon size={20} className="text-teal-600 shrink-0" />
                                              </div>
                                            );
                                        })()}
                                        {(() => {
                                            let rule = routing?.RoutingRule?.ActivityRules?.find(r => r.ActivityRuleType === "ActiveOnDayOfWeekRule");
                                            if (rule === undefined) return null;                                            
                                            if (!rule || !rule.ActiveWeekDays) return null;
                                            const weekConfig = [
                                              { name: "Monday", char: "M" },
                                              { name: "Tuesday", char: "T" },
                                              { name: "Wednesday", char: "W" },
                                              { name: "Thursday", char: "T" },
                                              { name: "Friday", char: "F" },
                                              { name: "Saturday", char: "S" },
                                              { name: "Sunday", char: "S" }
                                            ];
                                            return (                                              
                                                  <div style={{ display: 'flex', gap: '4px' }}>
                                                    {weekConfig.map(day => {
                                                      const isActive = rule!.ActiveWeekDays?.includes(day.name);
                                                      return (
                                                        <div 
                                                          key={day.name} 
                                                          title={day.name}
                                                          style={{ 
                                                            width: '20px', 
                                                            height: '20px', 
                                                            borderRadius: '9999px', 
                                                            backgroundColor: isActive ? '#3b82f6' : '#f1f5f9',
                                                            border: isActive ? '1px solid #2563eb' : '1px solid #cbd5e1',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '10px',                                                            
                                                            color: isActive ? 'white' : '#64748b'
                                                          }}
                                                        >
                                                          {day.char}
                                                        </div>
                                                      );
                                                    })}
                                                  </div>                                               
                                            );
                                          })()}
                                    </div>
                                  </td>
                                </tr>
                                {routing?.RoutingRule?.VolumeSharing && (
                                  <tr>
                                    <td className="w-[100px] text-slate-500 font-bold uppercase align-top pt-2">Volume Sharing</td>
                                    <td className="w-[10px] text-slate-400 font-bold align-top pt-2">:</td>
                                    <td className="pl-2 pt-1">
                                      <VolumeSharingDonut sharing={routing.RoutingRule.VolumeSharing} />
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
  );
};

export default RoutingSection;